/**
 * The Quality tab's scope, above its views (overview, Tests and coverage, a check): "All services / <service>" (the
 * overview read again for that service only, `GET /quality?service=`; the checks then run on it only, the other
 * services keeping their last results) and the services that are not deployed (Apps › Deploy this service, off): still
 * checked and shown, but they don't block Publish. Shown only in a project with more than one service, or when one is
 * not deployed. In the workspace the picker sits at the right end of the tab's title line (`slot`, a portal); the note
 * stays above the view.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { NOT_DEPLOYED_TIP } from '../../utils/deployFlags';

export const ALL_SERVICES = '';

/** The note for the services not deployed, in the scope `service` ('' = all): null when there is nothing to say. */
export function notDeployedNote(off, service) {
  const names = off || [];
  if (service) { return names.includes(service) ? `${service} is not deployed: its checks run and show here, but they don't block Publish, except secrets and licences for Git and GitOps.` : null; }
  if (!names.length) { return null; }
  return `Not deployed: ${names.join(', ')}. ${names.length === 1 ? 'Its' : 'Their'} checks run and show here, but don't block Publish, except secrets and licences for Git and GitOps.`;
}

export default function QualityScope({ services, off = [], service = ALL_SERVICES, onChange, onOpenApps = null, slot = null }) {
  const names = [...new Set(services || [])];
  const note = notDeployedNote(off, service);
  if (names.length < 2 && !note) { return null; }
  const pick = names.length > 1 && (
    <label className="quality-scope-pick">
      <span className="quality-scope-label">Checks of</span>
      <select aria-label="Checks of" className="quality-input is-sm" data-testid="quality-scope-service" onChange={(e) => onChange(e.target.value)} value={service}>
        <option value={ALL_SERVICES}>All services</option>
        {names.map((name) => <option key={name} value={name}>{off.includes(name) ? `${name} (not deployed)` : name}</option>)}
      </select>
    </label>
  );
  if (slot && !note) { return pick ? createPortal(pick, slot) : null; }
  return (
    <div className="quality-scope" data-testid="quality-scope">
      {pick && (slot ? createPortal(pick, slot) : pick)}
      {note && (
        <span className="quality-muted quality-small" data-testid="quality-scope-not-deployed" title={NOT_DEPLOYED_TIP}>
          {note}
          {onOpenApps && <> <button className="quality-link" onClick={() => onOpenApps(service || off[0])} type="button">Change in Apps</button></>}
        </span>
      )}
    </div>
  );
}

QualityScope.propTypes = {
  // The services switched off (short names).
  off: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  // (service) => opens Apps on that service (its Deploy switch); none: no link.
  onOpenApps: PropTypes.func,
  service: PropTypes.string,
  services: PropTypes.arrayOf(PropTypes.string),
  // The element the picker is portalled into (the workspace's title line, WorkspaceTitle `toolsRef`); none: inline.
  slot: PropTypes.object,
};
